<div class="h-full w-full overflow-y-auto custom-scrollbar p-6 lg:p-10 bg-body-background">
  <div class="max-w-4xl mx-auto flex flex-col gap-8 pb-12">

    <header class="flex flex-col gap-2">
      <h1 class="text-3xl font-bold text-heading tracking-tight">{{ config.texts.settingsTitle }}</h1>
      <p class="text-secondary-text">{{ config.texts.settingsSubtitle }}</p>
    </header>

    @if (activeProject(); as project) {

    <section class="bg-component-inner border border-primary-border rounded-xl p-6 shadow-sm flex flex-col gap-4">
      <h2 class="text-xl font-bold text-heading border-b border-secondary-border pb-3">{{ config.texts.infoSectionTitle
        }}</h2>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
        <div class="flex flex-col items-start">
          <span class="text-sm font-semibold text-secondary-text mb-1">{{ config.texts.infoKey }}</span>
          <div>
            <nz-tag class="!m-0 font-mono text-primary-text border-secondary-border bg-alt-background">{{ project.key
              }}</nz-tag>
          </div>
        </div>
        <div class="flex flex-col items-start">
          <span class="text-sm font-semibold text-secondary-text mb-1">{{ config.texts.infoId }}</span>
          <div class="flex items-center gap-2">
            <nz-tag class="!m-0 font-mono text-primary-text border-secondary-border bg-alt-background">{{ project.id
              }}</nz-tag>
            <button class="text-secondary-text hover:text-primary transition-colors focus:outline-none"
                    (click)="copyWorkspaceId(project.id)"
                    title="Copy to clipboard">
              <i class="fas fa-copy"></i>
            </button>
          </div>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-secondary-text mb-1">{{ config.texts.infoCreated }}</span>
          <span class="text-primary-text">{{ project.createdDate | date:'medium' }} by {{ project.createdBy }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-secondary-text mb-1">{{ config.texts.infoUpdated }}</span>
          <span class="text-primary-text">{{ project.updatedDate | date:'medium' }}</span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-secondary-text mb-1">{{ config.texts.infoAiTokens }}</span>
          <span class="text-primary-text font-mono">{{ project.aiTokenUsed | number }}
            <span class="text-secondary-text text-xs ml-1">(Limit: {{ project.aiUsageTokenLimit === -1 ? 'Unlimited' :
              project.aiUsageTokenLimit }})</span>
          </span>
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-semibold text-secondary-text mb-1">{{ config.texts.infoAiCost }}</span>
          <span class="text-primary-text font-mono">{{ project.aiTokensCost | currency:'USD' }}</span>
        </div>
      </div>

      @if (project.aiProviderSettings?.['providers']?.length) {
      <div class="mt-4 pt-4 border-t border-secondary-border">
        <span class="text-sm font-semibold text-secondary-text block mb-3">{{ config.texts.infoAiProviders }}</span>
        <div class="flex flex-wrap gap-2">
          @for (provider of project.aiProviderSettings?.['providers']; track provider.key) {
          <nz-tag [nzColor]="provider.key === project.aiProviderSettings?.['defaultProvider'] ? 'blue' : 'default'">
            <i class="fas fa-microchip mr-1"></i> {{ provider.key | titlecase }} - {{ provider.value.model }}
            @if (provider.key === project.aiProviderSettings?.['defaultProvider']) {
            (Default)
            }
          </nz-tag>
          }
        </div>
      </div>
      }
    </section>

    <section class="bg-component-inner border border-primary-border rounded-xl p-6 shadow-sm flex flex-col gap-4">
      <h2 class="text-xl font-bold text-heading border-b border-secondary-border pb-3">{{
        config.texts.generalSectionTitle }}</h2>

      <div class="flex flex-col gap-2 mt-2">
        <label class="text-sm font-semibold text-primary-text">{{ config.texts.nameLabel }}</label>
        <input nz-input
               type="text"
               [ngModel]="editName()"
               (ngModelChange)="editName.set($event)"
               class="w-full bg-input-background border border-secondary-border rounded-lg px-4 py-2 text-primary-text focus:outline-none focus:ring-1 focus:ring-focus-ring focus:border-focus-ring transition-colors" />
      </div>

      <div class="flex flex-col gap-2 mt-2">
        <label class="text-sm font-semibold text-primary-text">{{ config.texts.descriptionLabel }}</label>
        <textarea nz-input
                  rows="3"
                  [ngModel]="editDescription()"
                  (ngModelChange)="editDescription.set($event)"
                  class="w-full bg-input-background border border-secondary-border rounded-lg px-4 py-2 text-primary-text focus:outline-none focus:ring-1 focus:ring-focus-ring focus:border-focus-ring transition-colors custom-scrollbar resize-y"></textarea>
      </div>

      <div class="mt-2 text-right">
        <button (click)="saveGeneralSettings()"
                [disabled]="isSavingGeneral() || !editName().trim()"
                class="bg-primary hover:bg-hover-primary text-primary-button-text font-semibold rounded-lg px-6 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring shadow-sm disabled:opacity-50 flex items-center gap-2 ml-auto">
          @if (isSavingGeneral()) { <i class="fas fa-circle-notch fa-spin"></i> }
          {{ config.texts.saveChanges }}
        </button>
      </div>
    </section>

    <section class="bg-component-inner border border-primary-border rounded-xl p-6 shadow-sm flex flex-col gap-4">
      <h2 class="text-xl font-bold text-heading border-b border-secondary-border pb-3">{{
        config.texts.modulesSectionTitle }}</h2>

      <div class="flex items-center justify-between py-2 border-b border-secondary-border">
        <div class="flex flex-col">
          <span class="font-semibold text-primary-text">{{ config.texts.authModuleLabel }}</span>
          <span class="text-sm text-secondary-text">{{ config.texts.authModuleDesc }}</span>
        </div>
        <nz-switch [ngModel]="hasAuth()"
                   (ngModelChange)="hasAuth.set($event)"></nz-switch>
      </div>

      <div class="flex items-center justify-between py-2">
        <div class="flex flex-col">
          <span class="font-semibold text-primary-text">{{ config.texts.paymentModuleLabel }}</span>
          <span class="text-sm text-secondary-text">{{ config.texts.paymentModuleDesc }}</span>
        </div>
        <nz-switch [ngModel]="hasPayment()"
                   (ngModelChange)="hasPayment.set($event)"></nz-switch>
      </div>

      <div class="mt-4 text-right">
        <button (click)="saveModulesSettings()"
                [disabled]="isSavingModules()"
                class="bg-primary hover:bg-hover-primary text-primary-button-text font-semibold rounded-lg px-6 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring shadow-sm disabled:opacity-50 flex items-center gap-2 ml-auto">
          @if (isSavingModules()) { <i class="fas fa-circle-notch fa-spin"></i> }
          {{ config.texts.saveChanges }}
        </button>
      </div>
    </section>

    <section class="bg-component-inner border border-primary-border rounded-xl p-6 shadow-sm flex flex-col gap-4">
      <h2 class="text-xl font-bold text-heading border-b border-secondary-border pb-3">{{
        config.texts.templatesSectionTitle }}</h2>

      <div class="flex flex-col gap-4 mt-2">
        @for (tpl of templatesDefinition; track tpl.id) {
        <div class="flex items-center justify-between bg-alt-background border border-secondary-border rounded-lg p-4">
          <div class="flex items-center gap-4">
            <div
                 class="w-10 h-10 rounded bg-component-inner border border-secondary-border flex items-center justify-center text-primary-text shrink-0">
              <i class="fas fa-code"></i>
            </div>
            <div class="flex flex-col">
              <span class="font-bold text-primary-text">{{ tpl.name }}</span>
              <span class="text-sm text-secondary-text">{{ tpl.description }}</span>
            </div>
          </div>
          <nz-switch [ngModel]="isTemplateActive(tpl.id)"
                     (ngModelChange)="onTemplateToggle(tpl.id, $event)">
          </nz-switch>
        </div>
        }
      </div>

      <div class="mt-4 text-right">
        <button (click)="saveTemplatesSettings()"
                [disabled]="isSavingTemplates()"
                class="bg-primary hover:bg-hover-primary text-primary-button-text font-semibold rounded-lg px-6 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring shadow-sm disabled:opacity-50 flex items-center gap-2 ml-auto">
          @if (isSavingTemplates()) { <i class="fas fa-circle-notch fa-spin"></i> }
          {{ config.texts.saveChanges }}
        </button>
      </div>
    </section>

    <section class="bg-component-inner border border-primary-border rounded-xl p-6 shadow-sm flex flex-col gap-4">
      <h2 class="text-xl font-bold text-heading border-b border-secondary-border pb-3">{{
        config.texts.membersSectionTitle }}</h2>

      <nz-table #membersTable
                [nzData]="workspaceMembers()"
                nzSize="small"
                class="w-full mt-2 border border-secondary-border rounded overflow-hidden">
        <thead>
          <tr class="bg-alt-background">
            <th class="!bg-alt-background text-primary-text font-semibold">{{ config.texts.membersTableUser }}</th>
            <th class="!bg-alt-background text-primary-text font-semibold">{{ config.texts.membersTableRole }}</th>
            <th class="!bg-alt-background text-primary-text font-semibold">{{ config.texts.membersTableStatus }}</th>
          </tr>
        </thead>
        <tbody>
          @for (member of membersTable.data; track member.id) {
          <tr class="hover:bg-hover-default transition-colors">
            <td class="!bg-component-inner border-t border-secondary-border text-primary-text">
              <div class="flex items-center gap-3">
                <div
                     class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">
                  {{ member.name.charAt(0) }}
                </div>
                <span class="font-medium">{{ member.name }}</span>
              </div>
            </td>
            <td class="!bg-component-inner border-t border-secondary-border text-secondary-text">{{ member.role }}</td>
            <td class="!bg-component-inner border-t border-secondary-border">
              <nz-tag [nzColor]="member.status === 'Active' ? 'success' : 'default'">{{ member.status }}</nz-tag>
            </td>
          </tr>
          }
        </tbody>
      </nz-table>
    </section>

    <section
             class="bg-component-inner border border-danger rounded-xl p-6 shadow-sm flex flex-col gap-4 relative overflow-hidden">
      <div class="absolute inset-0 bg-danger opacity-5 pointer-events-none"></div>

      <h2 class="text-xl font-bold text-danger border-b border-danger/20 pb-3 relative z-10">
        <i class="fas fa-exclamation-triangle mr-2"></i>{{ config.texts.deleteWorkspaceTitle }}
      </h2>

      <div class="flex flex-col gap-4 mt-2 relative z-10">
        <div class="flex flex-col">
          <span class="font-bold text-primary-text">{{ config.texts.dangerSectionTitle }}</span>
          <span class="text-secondary-text">{{ config.texts.deleteWorkspaceDesc }}</span>
        </div>

        <div class="w-full bg-alt-background border border-secondary-border rounded-lg p-4 flex flex-col gap-3">
          <label class="text-sm text-primary-text">
            {{ config.texts.deleteConfirmLabel }}
            <span class="font-mono font-bold text-text-main ml-1 select-all bg-component-inner px-1 rounded">{{
              project.key }}</span>
          </label>
          <input nz-input
                 type="text"
                 [ngModel]="deleteConfirmKey()"
                 (ngModelChange)="deleteConfirmKey.set($event)"
                 [placeholder]="project?.key"
                 class="w-full bg-input-background border border-danger/50 rounded-lg px-4 py-2 text-primary-text focus:outline-none focus:ring-1 focus:ring-danger focus:border-danger transition-colors font-mono text-sm" />

          <button (click)="deleteWorkspace()"
                  [disabled]="isDeleting() || !canDelete()"
                  class="bg-danger hover:bg-hover-danger text-white font-semibold rounded-lg px-6 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-danger shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2">
            @if (isDeleting()) { <i class="fas fa-circle-notch fa-spin"></i> }
            <i class="fas fa-trash-alt"></i>
            {{ config.texts.deleteButton }}
          </button>
        </div>
      </div>
    </section>

    }
  </div>
</div>